'use client';

import { cn } from '@workspace/ui/lib';
import { Check } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useLayoutEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useWizardStore } from '@/stores/wizardStore';

interface StepperStepProps {
  title: string;
  index: number;
  isCompleted: boolean;
  isActive: boolean;
}

export default function StepperStep({ title, index, isCompleted, isActive }: StepperStepProps) {
  const stepRef = useRef<HTMLDivElement>(null);
  const setStepWidth = useWizardStore((s) => s.setStepWidth);
  const stepWidth = useWizardStore((s) => s.steps[index]?.width);
  const isLoading = useAuthStore((s) => s.isLoading);
  const { theme } = useTheme();

  useLayoutEffect(() => {
    // Skip if loading or width already known
    if (isLoading || stepWidth !== undefined) return;

    // Calculate width if we have a ref
    if (stepRef.current) {
      setStepWidth(index, stepRef.current.offsetWidth);
    }
  }, [index, setStepWidth, stepWidth, isLoading]);

  // Don't render step while loading
  if (isLoading) return null;

  return (
    <div ref={stepRef} className="flex items-center gap-4">
      <div className="xs:h-8 xs:w-8 relative flex h-6 w-6 items-center justify-center">
        {isCompleted ? (
          <span className="text-secondary absolute inset-0 z-10 flex items-center justify-center rounded-full bg-transparent dark:text-neutral-100">
            <Check size={24} className="xs:size-6 size-4" />
          </span>
        ) : (
          <span
            className={cn(
              'absolute inset-0 z-10 flex items-center justify-center text-xs font-medium',
              isActive ? 'text-secondary dark:text-neutral-100' : 'text-grey-500 dark:text-grey-600'
            )}
          >
            {index + 1}
          </span>
        )}
        {/* SVG Circle for dashed border */}
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 32 32">
          <circle
            cx="16"
            cy="16"
            r="14"
            fill="none"
            stroke={
              isActive
                ? theme === 'dark'
                  ? 'var(--color-neutral-100)'
                  : 'var(--color-secondary)'
                : isCompleted
                  ? 'transparent'
                  : theme === 'dark'
                    ? 'var(--color-grey-600)'
                    : 'var(--color-grey-500)'
            }
            strokeWidth="1"
            strokeDasharray={isActive ? '4 4' : '0'}
          />
        </svg>
      </div>
      <span
        className={cn(
          'hidden text-center text-xs xl:block',
          isActive || isCompleted ? 'text-secondary dark:text-neutral-100' : 'text-grey-600'
        )}
      >
        {title}
      </span>
    </div>
  );
}
